<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  ariaLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['change']);

const { t } = useI18n();

const modelValue = defineModel({
  type: Boolean,
  default: false,
});

const resolvedAriaLabel = computed(() => String(props.ariaLabel || '').trim());

const updateValue = () => {
  if (props.disabled) return;

  const nextValue = !modelValue.value;
  modelValue.value = nextValue;
  emit('change', nextValue);
};
</script>

<template>
  <button
    type="button"
    class="group relative inline-flex h-11 w-11 flex-shrink-0 touch-manipulation select-none items-center justify-center rounded-lg p-0 outline-none transition-none hover:bg-n-alpha-2 active:bg-n-alpha-1 focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2 focus-visible:ring-offset-n-slate-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:active:bg-transparent"
    role="switch"
    :aria-checked="modelValue"
    :aria-label="resolvedAriaLabel || undefined"
    :disabled="props.disabled"
    @click="updateValue"
  >
    <span v-if="!resolvedAriaLabel" class="sr-only">
      {{ t('SWITCH.TOGGLE') }}
    </span>
    <span
      aria-hidden="true"
      class="relative h-4 w-7 rounded-full motion-safe:transition-colors motion-safe:duration-200 motion-safe:ease-in-out"
      :class="modelValue ? 'bg-n-brand' : 'bg-n-slate-10'"
    >
      <span
        class="absolute top-1/2 block h-3 w-3 -translate-y-1/2 rounded-full bg-n-background shadow-md motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-[cubic-bezier(0.34,1.56,0.64,1)] ltr:left-0.5 rtl:right-0.5"
        :class="
          modelValue
            ? 'ltr:translate-x-3 rtl:-translate-x-3'
            : 'ltr:translate-x-0 rtl:translate-x-0'
        "
      />
    </span>
  </button>
</template>
